import {
  doc,
  collection,
  setDoc,
  serverTimestamp,
  updateDoc,
  getDoc,
  DocumentReference,
  Timestamp,
} from '@firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { ref, uploadBytesResumable, UploadTask } from 'firebase/storage';

import { removeUndefined } from '../../utils';
import { db, storage } from '../../firebase-config';
import { UPLOAD_STATUS, STORAGE_PATH_MAP } from './constants.ts';
import type { FileUploadData, FileUploadType, UploadedFile } from './types.ts';
import { generateCustomMetadata, getFileUploadUrl, handleFileUploadError } from './utils.ts';

interface UploadParams<TFileUploadType extends FileUploadType> extends FileUploadData<TFileUploadType> {
  fileRef?: DocumentReference;
  signal?: AbortSignal;
}

type UploadReturn = {
  id: string;
} & (
  | {
      success: false;
      error: unknown;
    }
  | {
      success: true;
      uploadTask: UploadTask;
    }
);

const handleUploadError = async <TFileUploadType extends FileUploadType>({
  fileEventDocRef,
  error,
  fileData,
}: {
  fileEventDocRef: DocumentReference;
  error: Error;
  fileData: UploadParams<TFileUploadType>;
}) => {
  handleFileUploadError({
    error,
    fileData,
    issueDescription: 'file upload',
  });

  if (error instanceof FirebaseError) {
    if (error.code === 'storage/canceled') {
      await updateDoc(fileEventDocRef, {
        status: UPLOAD_STATUS.CANCELLED,
        history: { CANCELLED: { startedAt: serverTimestamp() } },
      }).catch((updateDocError) => {
        handleFileUploadError({
          fileData,
          error: updateDocError,
          issueDescription: 'task document update',
        });
      });

      return;
    }
  }

  await updateDoc(fileEventDocRef, {
    status: UPLOAD_STATUS.FAILED,
    history: { FAILED: { startedAt: serverTimestamp() } },
  }).catch((updateDocError) => {
    handleFileUploadError({
      fileData,
      error: updateDocError,
      issueDescription: 'task document update',
    });
  });
};

const cleanupSignal = ({ signal, fn }: { signal?: AbortSignal; fn: () => unknown }) => {
  signal?.removeEventListener('abort', fn);
};

const upload = async <TFileUploadType extends FileUploadType>({
  fileRef,
  file,
  signal,
  ...fileData
}: UploadParams<TFileUploadType>): Promise<UploadReturn> => {
  if (fileRef) {
    const fileEventDoc = await getDoc(fileRef);

    if (fileEventDoc.exists()) {
      return { success: false, id: fileRef.id, error: new Error('File already exists') };
    }
  }

  const fileEventDocRef = fileRef || doc(collection(db, 'file_uploads'));
  const now = serverTimestamp();

  const { type, ownerId, fileName, uploaderId, metadata: initialMetadata } = fileData;

  const { patientId } = initialMetadata || {};

  const baseStoragePath = STORAGE_PATH_MAP[type]?.({ patientId });

  if (!baseStoragePath) {
    throw new Error('Invalid file upload type');
  }

  const storagePath = `${baseStoragePath}/${fileEventDocRef.id}`;

  const fileUploadDocId = fileEventDocRef.id;

  try {
    await setDoc(fileEventDocRef, {
      fileName,
      fileUploadType: type,
      fileType: file.type,
      filePath: storagePath,
      metadata: initialMetadata,
      status: UPLOAD_STATUS.UPLOADING,
      history: { CREATED: { startedAt: now as Timestamp } },
      participants: removeUndefined({ patientId, uploaderId, ownerId }),
      createdAt: now as Timestamp,
    } satisfies Omit<UploadedFile<TFileUploadType>, 'id'>);
  } catch (createDocError) {
    handleFileUploadError({
      error: createDocError,
      fileData: { file, ...fileData },
      issueDescription: 'task document creation',
    });

    return { success: false, id: fileEventDocRef.id, error: new Error('Failed to create file upload document') };
  }

  const { type: _, ...fileMetadata } = fileData;

  // FUTURE: add dataType [isMedical, isGeneral, etc]. Maybe replacement for 'generalData'
  const metadata = generateCustomMetadata<TFileUploadType>({
    fileUploadDocId,
    fileUploadType: type,
    ...fileMetadata,
  });

  const uploadTask = uploadBytesResumable(ref(storage, getFileUploadUrl(storagePath)), file, metadata);

  const cancelTask = () => {
    cleanupSignal({ signal, fn: cancelTask });

    return uploadTask.cancel();
  };

  signal?.addEventListener('abort', cancelTask);

  uploadTask.on(
    'state_changed',
    async (snapshot) => {
      if (snapshot.state === 'canceled') {
        cleanupSignal({ signal, fn: cancelTask });

        return;
      }
    },
    async (error) => {
      await handleUploadError({
        error,
        fileEventDocRef,
        fileData: { file, ...fileData },
      });

      cleanupSignal({ signal, fn: cancelTask });

      return { success: false, id: fileEventDocRef.id, error };
    },
    async () => {
      await updateDoc(fileEventDocRef, {
        status: UPLOAD_STATUS.UPLOADED,
      });

      cleanupSignal({ signal, fn: cancelTask });

      return { success: true, id: fileEventDocRef.id, uploadTask };
    },
  );

  return { success: true, id: fileEventDocRef.id, uploadTask };
};

export const useFileUpload = () => {
  const handleUpload = async (params: Parameters<typeof upload>[0]): ReturnType<typeof upload> => {
    if (params.fileRef) {
      const fileEventDoc = await getDoc(params.fileRef);

      if (fileEventDoc.exists()) {
        return { success: false, id: params.fileRef.id, error: new Error('File already exists') };
      }
    }

    return upload(params);
  };

  return { upload: handleUpload } as const;
};
